import { useGenerationGuard } from '@/contexts/generation-guard-context';
import { useMyCohorts } from '@/hooks/queries/cohorts';
import useGetPrompts from '@/hooks/queries/prompts/useGetPrompts';
import useRunPrompt from '@/hooks/queries/prompts/useRunPrompt';
import {
  useCreateOrUpdateVariable,
  useGetVariables,
} from '@/hooks/queries/variables';
import {
  myVariablesKeys,
  useMyVariables,
} from '@/hooks/queries/variables/variables-v2';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useChatMessageDelivery } from '@/hooks/useChatMessageDelivery';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import { useNavigation } from '@/hooks/useNavigation';
import { RunActionState } from '@/hooks/useRunActionState';
import { useSoulMapDocumentReveal } from '@/hooks/useSoulMapDocumentReveal';
import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments';
import { useLoaderStore } from '@/store/use-loader-store';
import { useTenantStore } from '@/store/use-tenant-store';
import { useUserStore } from '@/store/use-user-store';
import { useChatStore } from '@/store/useChatStore';
import { useDocumentStore } from '@/store/useDocumentStore';
import { PromptData } from '@/types/agent';
import {
  AFTER_TIMEOUT_RECOVERY_DELAYS_MS,
  ApiPost,
  getInterruptedUserMessageId,
  getPostsReconciliationKey,
  hasPersistedReplyForLatestTurn,
  INTERRUPTED_RESPONSE_ERROR,
  isLocalConversationComplete,
  mapPostsToMessages,
  NO_RESPONSE_DELIVERY_ERROR,
  refetchUntilAssistantReply,
  resolveTurnErrorMessage,
  shouldRebuildFromServerPosts,
  SOULMAP_STREAM_RECOVERY_DELAYS_MS,
} from '@/utils/conversation-reconciliation';
import {
  findSoulMapDocumentAnchorId,
  getSoulMapDocumentPhase,
  getSoulMapHeaderState,
  isSoulMapDocumentInProgress,
} from '@/utils/soulmap-document-view';
import { shouldUseSoulMapLongSession } from '@/utils/soulmap-learning-path';
import {
  getSoulMapProfileFieldErrorsFromApi,
  getStoredBirthPlaceLabel,
  hasConfirmedSoulMapBirthDetails,
  parseMyVariablesList,
  resolveSoulMapProfileForm,
  saveSoulMapProfile,
  SoulMapProfileForm,
} from '@/utils/soulmap-profile';
import {
  hasSoulMapBirthDetailsOptOuts,
  haveSoulMapBirthDetailsChanged,
  isSoulMapBirthDetailsOptedOut,
} from '@/utils/soulmap-profile-confirmation';
import { SOULMAP_CHAT_RESPONSE_TIMEOUT_MS } from '@/utils/soulmap-session-refresh';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import useGetPosts from '../../../hooks/queries/practices/useGetPosts';
import useGetPracticeById from '../../../hooks/queries/practices/useGetPracticeById';
import useRunPractice from '../../../hooks/queries/practices/useRunPractice';
import { useTTS } from '../../../hooks/useTTS';
import PracticeService from '../../../services/practice-service';
import websocketService, {
  EMPTY_RESPONSE_ERROR,
  RESPONSE_TIMEOUT_ERROR,
  SoulMapDocumentQueuedEvent,
  STREAM_TIMEOUT_ERROR,
  StreamErrorDetails,
} from '../../../services/websocket-service';
import InputBox from '../input/input-box';
import MessageList from '../messages/message-list';
import { ChatHeader } from './chat-header';
import { ChatSidepanel } from './chat-sidepanel';
import SettingsDrawer from './settings-drawer';
import { SoulMapDocumentCard } from './soul-map-document-card';
import {
  SoulMapProfileConfirmModal,
  SoulMapProfileModalMode,
} from './soul-map-profile-confirm-modal';

const welcomeMessage = {
  id: 'welcome',
  question: 'Welcome to Mentora LiFT! Are you ready to get started?',
};

/** How long a birth-details confirmation waits for the report to start updating. */
const AWAITING_DOCUMENT_REFRESH_TIMEOUT_MS = 30_000;

interface ChatContainerProps {
  fetchingPrompts: boolean;
  runActionState: RunActionState;
  fetchingLatetConversation?: boolean;
  restartPractice: (title: string) => void;
  selectedPrompt: PromptData | null;
  onLeaveChat?: () => void;
  showRestartButton?: boolean;
  /** Hidden in the Soul Map consumer flow, where the landing form collects these first. */
  showProfileConfirmation?: boolean;
}

type StreamTurnStatus = 'streaming' | 'completed' | 'failed';

interface ActiveStreamTurn {
  generation: number;
  streamMessageId: string;
  userMessageId: string;
  status: StreamTurnStatus;
}

interface RecoveryInFlight {
  turn: ActiveStreamTurn | null;
  promise: Promise<boolean>;
}

function ChatContainer({
  fetchingPrompts,
  runActionState,
  fetchingLatetConversation = false,
  restartPractice,
  selectedPrompt,
  onLeaveChat,
  showRestartButton = true,
  showProfileConfirmation = true,
}: ChatContainerProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [streamingTargetText, setStreamingTargetText] = useState('');
  const queryClient = useQueryClient();
  const {
    messages,
    isThinking,
    enableTTS,
    addMessage,
    updateMessage,
    removeMessage,
    setThinking,
    setMessageDeliveryStatus,
    setResponseInProgress,
    setStreamingMessageId,
    finishAssistantResponse,
    streamingMessageId,
    clearMessages,
    isLoading,
    addMessages,
  } = useChatStore();
  const reconciledPostsKeyRef = useRef<string | null>(null);
  const hasShownInterruptedToastRef = useRef(false);
  const streamingTargetTextRef = useRef('');
  const activeStreamTurnRef = useRef<ActiveStreamTurn | null>(null);
  const recoveryAbortGenerationRef = useRef(0);
  const recoveryInFlightRef = useRef<RecoveryInFlight | null>(null);
  const recentChatsRefreshedForRef = useRef<number | null>(null);
  // LE-338: the saved post behind each local user message, so Retry asks the
  // server to answer that post rather than sending the text again.
  const userPostIdsRef = useRef(new Map<string, number>());
  // Messages whose saved post the server refused to retry (already answered
  // or no longer last); their Retry falls back to resending the text.
  const retryByTextOnlyRef = useRef(new Set<string>());
  // A message shown but waiting for the previous turn to end before sending.
  const pendingSendRef = useRef(false);
  const { guardAction } = useGenerationGuard();

  const {
    playingMessageId,
    loadingMessageId,
    stopSpeaking,
    togglePlayPause,
    currentVoice,
    setCurrentVoice,
    availableVoices,
    speakResponse,
  } = useTTS();
  const runPracticeMutation = useRunPractice();
  const location = useLocation();
  const { navigateToHome } = useNavigation();
  const queryParams = new URLSearchParams(location.search);
  const urlActionTitle = queryParams.get('action');
  const urlPracticeId = queryParams.get('id');
  // The Hub tab the workout was opened from, so Back returns to it.
  const urlHubTab = queryParams.get('tag');
  const resolvedPracticeId = urlPracticeId || runActionState.id || null;
  const resolvedPracticeIdNum = Number(resolvedPracticeId);
  const actionTitle = urlActionTitle || runActionState.actionTitle;
  const { data: prompts } = useGetPrompts();
  const isSoulMapPractice = useMemo(
    () =>
      shouldUseSoulMapLongSession({
        actionTitle,
        promptName: selectedPrompt?.name,
      }),
    [actionTitle, selectedPrompt?.name]
  );

  const {
    data: practice,
    error: practiceError,
    isLoading: isPracticeLoading,
  } = useGetPracticeById(resolvedPracticeIdNum) as {
    data?: { created_at?: string };
    error: unknown;
    isLoading: boolean;
  };
  // Practices are visible only to their owner (LE-422); anyone else gets a 404.
  const isPracticeNotFound =
    axios.isAxiosError(practiceError) && practiceError.response?.status === 404;

  const effectiveLongSession = isSoulMapPractice;
  const messageDelivery = useChatMessageDelivery(
    effectiveLongSession ? SOULMAP_CHAT_RESPONSE_TIMEOUT_MS : undefined
  );
  const [awaitingDocumentRefresh, setAwaitingDocumentRefresh] = useState(false);
  const soulMapDocuments = useSoulMapDocuments(
    String(resolvedPracticeId || ''),
    messages,
    isSoulMapPractice && !isPracticeLoading && !isPracticeNotFound,
    { pollWhileRefreshing: awaitingDocumentRefresh }
  );
  const { refetch: refetchSoulMapDocuments } = soulMapDocuments;

  const handleSoulMapDocumentQueued = useCallback(
    (event: SoulMapDocumentQueuedEvent) => {
      if (event.practice_id !== resolvedPracticeIdNum) {
        return;
      }

      void refetchSoulMapDocuments();
    },
    [refetchSoulMapDocuments, resolvedPracticeIdNum]
  );

  const soulMapDocumentStatus = isSoulMapPractice
    ? soulMapDocuments.data
    : undefined;
  const { tenantSlug } = useActiveTenant();
  // Live cohorts, so admin changes apply without a re-login.
  const { data: myCohortsData, isFetching: isFetchingMyCohorts } = useMyCohorts(
    {
      enabled:
        isSoulMapPractice &&
        showProfileConfirmation &&
        hasSoulMapBirthDetailsOptOuts(),
    }
  );
  const birthDetailsOptedOut = useMemo(
    () => isSoulMapBirthDetailsOptedOut(tenantSlug, myCohortsData?.results),
    [tenantSlug, myCohortsData?.results]
  );
  const documentPhaseOptions = {
    treatExpiredAsPreparing: awaitingDocumentRefresh,
    // Opted-out students never see the birth-details form, so an expired
    // document is offered again instead of asking them to review it.
    regenerateExpired: birthDetailsOptedOut,
  };
  const showSoulMapHeaderPill =
    getSoulMapHeaderState(soulMapDocumentStatus, documentPhaseOptions) !==
    'hidden';
  const soulMapDocumentPhase = getSoulMapDocumentPhase(
    soulMapDocumentStatus,
    documentPhaseOptions
  );

  useEffect(() => {
    if (!awaitingDocumentRefresh) return;

    const status = soulMapDocumentStatus?.status;
    if (isSoulMapDocumentInProgress(status) || status === 'failed') {
      setAwaitingDocumentRefresh(false);
    }
  }, [awaitingDocumentRefresh, soulMapDocumentStatus?.status]);

  // Never wait on "Updating birth profile…" forever: if the backend staged
  // nothing, show whatever status the report has.
  useEffect(() => {
    if (!awaitingDocumentRefresh) return;

    const timeoutId = window.setTimeout(
      () => setAwaitingDocumentRefresh(false),
      AWAITING_DOCUMENT_REFRESH_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [awaitingDocumentRefresh]);

  const revealSoulMapDocument = useSoulMapDocumentReveal(soulMapDocumentPhase);
  const soulMapDocumentAnchorId = useMemo(
    () =>
      soulMapDocumentPhase
        ? findSoulMapDocumentAnchorId(messages, soulMapDocumentStatus)
        : null,
    [soulMapDocumentPhase, messages, soulMapDocumentStatus]
  );
  const { selectedFiles } = useDocumentStore();
  const [isSavingMemory, setIsSavingMemory] = useState(false);
  const {
    data: posts,
    isSuccess,
    isLoading: isPostsLoading,
    isFetchedAfterMount: isPostsFetchedAfterMount,
  } = useGetPosts(resolvedPracticeIdNum);
  const initialisingChat =
    (fetchingPrompts && messages.length === 0) || isLoading;
  // Cached posts from an earlier visit can predate the whole conversation,
  // so the first render waits for the fetch this mount started.
  const needsInitialPostsLoad =
    messages.length === 0 &&
    (isPostsLoading ||
      (Boolean(resolvedPracticeIdNum) && !isPostsFetchedAfterMount));
  // Someone else's practice still returns an empty post list, so wait for the
  // practice itself before showing the chat; a 404 redirects below.
  const needsPracticeLoad = isPracticeLoading || isPracticeNotFound;
  const enableQuickActions =
    import.meta.env.VITE_ENABLE_QUICK_ACTIONS === 'true';
  const isKeyboardVisible = useKeyboardVisible();

  useEffect(() => {
    if (!streamingMessageId) {
      streamingTargetTextRef.current = '';
      setStreamingTargetText('');
    }
  }, [streamingMessageId]);

  useEffect(() => {
    if (!effectiveLongSession) return;

    websocketService.setLongSessionMode(true);
    return () => {
      websocketService.setLongSessionMode(false);
    };
  }, [effectiveLongSession]);

  const syncStreamingTargetText = useCallback((content: string) => {
    streamingTargetTextRef.current = content;
    setStreamingTargetText(content);
  }, []);

  const { config: tenantConfig } = useTenantStore();
  const { user } = useUserStore();
  const { data: variables, refetch: refetchVariables } = useGetVariables(
    {
      filters: {
        parent_object_id: Number(user?.id),
        parent_content_type: 'zenauth.zenuser',
      },
    },
    true
  );
  const { mutateAsync: saveUserVariable } = useCreateOrUpdateVariable({
    showToast: false,
    onSuccess: () => {
      refetchVariables();
      toast.success('Memory updated!');
    },
  });

  const userProfile = useMemo(() => {
    if (!enableQuickActions) return undefined;
    // temp fix: user-variables api is not returning user vairiabls if login through sso
    // TODO: remove this once user-variables api is fixed

    const fromVariables = variables?.results?.length
      ? variables.results
          .find((item) =>
            item.text_variables?.some((textVar) => textVar.title === 'profile')
          )
          ?.text_variables?.find((textVar) => textVar.title === 'profile')
      : undefined;

    return fromVariables;
  }, [variables?.results]);

  const profileConfirmationEnabled =
    isSoulMapPractice && showProfileConfirmation && !birthDetailsOptedOut;
  const [isProfileConfirmOpen, setIsProfileConfirmOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileServerErrors, setProfileServerErrors] = useState<
    ReturnType<typeof getSoulMapProfileFieldErrorsFromApi>
  >({});
  const profileConfirmResolvedRef = useRef(false);
  const {
    data: myVariablesData,
    isLoading: isLoadingMyVariables,
    isFetching: isFetchingMyVariables,
  } = useMyVariables(undefined, { enabled: profileConfirmationEnabled });
  const myVariables = useMemo(
    () => parseMyVariablesList(myVariablesData),
    [myVariablesData]
  );
  const soulMapProfileForm = useMemo(
    () => resolveSoulMapProfileForm(myVariables, { useDraft: false }),
    [myVariables]
  );
  const storedBirthPlaceLabel = useMemo(
    () => getStoredBirthPlaceLabel(myVariables),
    [myVariables]
  );
  // A stored place counts as "on file" even before the catalog lookup recovers
  // its coordinates, so the framing does not flip while that request runs.
  const birthDetailsOnFile = [
    Boolean(soulMapProfileForm.birthDate),
    Boolean(soulMapProfileForm.birthTime),
    Boolean(soulMapProfileForm.birthPlace) || Boolean(storedBirthPlaceLabel),
  ].filter(Boolean).length;
  const profileModalMode: SoulMapProfileModalMode =
    birthDetailsOnFile === 3
      ? 'confirm'
      : birthDetailsOnFile === 0
        ? 'setup'
        : 'partial';
  // The gate waits for fresh data before deciding whether to ask at all...
  const isLoadingSoulMapProfile =
    profileConfirmationEnabled &&
    (isLoadingMyVariables || isFetchingMyVariables || isFetchingMyCohorts);
  // ...but the modal body only blocks on a genuine first load, so the
  // post-save refetch cannot blank the form it is already showing.
  const isLoadingProfileForm =
    profileConfirmationEnabled && isLoadingMyVariables;

  useEffect(() => {
    if (!profileConfirmationEnabled) {
      profileConfirmResolvedRef.current = false;
      setIsProfileConfirmOpen(false);
      return;
    }

    if (profileConfirmResolvedRef.current) return;
    if (!resolvedPracticeId) return;
    if (isLoadingSoulMapProfile) return;
    // Wait for the chat itself so the modal lands on a settled page.
    if (initialisingChat || needsInitialPostsLoad) return;

    profileConfirmResolvedRef.current = true;

    if (hasConfirmedSoulMapBirthDetails(myVariables)) return;

    setIsProfileConfirmOpen(true);
  }, [
    profileConfirmationEnabled,
    resolvedPracticeId,
    isLoadingSoulMapProfile,
    initialisingChat,
    needsInitialPostsLoad,
    myVariables,
  ]);

  // Nothing is written, so the modal stays armed for the next visit.
  const handleProfileConfirmExit = useCallback(() => {
    setIsProfileConfirmOpen(false);
    navigateToHome();
  }, [navigateToHome]);

  const handleReviewBirthDetails = useCallback(() => {
    setIsProfileConfirmOpen(true);
  }, []);

  const handleProfileConfirmSubmit = useCallback(
    async (form: SoulMapProfileForm, presentedForm: SoulMapProfileForm) => {
      setIsSavingProfile(true);
      setProfileServerErrors({});
      try {
        const birthDetailsChanged = haveSoulMapBirthDetailsChanged(
          presentedForm,
          form
        );
        const hadDocument = Boolean(
          soulMapDocumentStatus &&
            soulMapDocumentStatus.status !== 'unavailable' &&
            soulMapDocumentStatus.status !== 'unsupported' &&
            soulMapDocumentStatus.status !== 'not_started'
        );

        await saveSoulMapProfile(form, myVariables, {
          persistDraft: false,
          markBirthConfirmed: true,
        });
        void queryClient.invalidateQueries({
          queryKey: myVariablesKeys.lists(),
        });

        // Confirmation itself kicks off recompute and regeneration. Do not
        // call retry — that endpoint is only for ordinary generation failures.
        if (hadDocument || birthDetailsChanged) {
          setAwaitingDocumentRefresh(true);
        }

        setIsProfileConfirmOpen(false);
        const refreshed = await refetchSoulMapDocuments();
        // Unchanged details on a ready or expired report stage nothing, so
        // render that status now instead of waiting for one that never comes.
        if (
          refreshed.isSuccess &&
          !isSoulMapDocumentInProgress(refreshed.data?.status)
        ) {
          setAwaitingDocumentRefresh(false);
        }

        if (hadDocument || birthDetailsChanged) {
          toast.success(
            'Your details were saved. We are updating your Soul Map.'
          );
        }
      } catch (error) {
        console.error('Failed to save Soul Map birth details:', error);
        const fieldErrors = getSoulMapProfileFieldErrorsFromApi(error);
        if (Object.keys(fieldErrors).length > 0) {
          setProfileServerErrors(fieldErrors);
        } else {
          toast.error('Could not save your details. Please try again.');
        }
      } finally {
        setIsSavingProfile(false);
      }
    },
    [myVariables, queryClient, refetchSoulMapDocuments, soulMapDocumentStatus]
  );

  const { mutateAsync: runPromptMutation } = useRunPrompt({
    onSuccess: (data) => {
      console.log('Summarization completed:', data);
    },
  });

  const handleSaveToMemory = useCallback(async () => {
    if (!messages.length) return;

    setIsSavingMemory(true);
    try {
      // Get existing memory
      const existingMemory = variables?.results?.find(
        (result) =>
          result.parent_content_type === 'zenauth.zenuser' &&
          result.parent_object_id === (user?.id ? Number(user.id) : null) &&
          result.text_variables?.some((v) => v.title === 'user.memory')
      );

      const existingMemoryText =
        existingMemory?.text_variables?.find((v) => v.title === 'user.memory')
          ?.value || '';

      // Get the summarization prompt
      const summarisePrompt = prompts?.results.find(
        (prompt) => prompt.name === 'Summarize an action agent (v2)'
      );

      if (!summarisePrompt) {
        console.error('Summarization prompt not found');
        return;
      }

      // Get all messages text and combine with existing memory
      const conversationText = messages
        .map((m) => `${m.isUser ? 'User' : 'Assistant'}: ${m.text}`)
        .join('\n');

      const combinedText = existingMemoryText
        ? `Previous Memory:\n${existingMemoryText}\n\nNew Conversation:\n${conversationText}`
        : conversationText;

      // Run summarization on the combined text
      const summary = await runPromptMutation({
        promptId: summarisePrompt.id,
        text: combinedText,
      });

      if (!summary) {
        console.error('No summary generated');
        return;
      }

      // Save the new summary as memory
      await saveUserVariable({
        data: {
          text_variables: [
            {
              title: 'memory',
              value: `${existingMemoryText}\n\n${summary}`,
            },
          ],
          parent_object_id: user?.id ? Number(user.id) : null,
          parent_content_type: 'zenauth.zenuser',
        },
      });

      // Refetch variables to update the UI
      refetchVariables();
    } catch (error) {
      console.error('Error saving memory:', error);
      // TODO: Add error handling/notification
    } finally {
      setIsSavingMemory(false);
    }
  }, [
    messages,
    variables?.results,
    user?.id,
    prompts?.results,
    runPromptMutation,
    saveUserVariable,
    refetchVariables,
  ]);

  const handleDeliveryFailure = useCallback(
    (userMessageId: string, error: string) => {
      finishAssistantResponse();
      messageDelivery.completeDelivery();
      // The delivery timer and the stream timeout can both report the same
      // turn; show the failure once.
      const liveUserMessage = useChatStore
        .getState()
        .messages.find((item) => item.id === userMessageId);
      if (liveUserMessage?.deliveryStatus === 'failed') {
        return;
      }
      setMessageDeliveryStatus(userMessageId, 'failed', error);
      toast.error(error);
    },
    [finishAssistantResponse, messageDelivery, setMessageDeliveryStatus]
  );

  // Recent Chats lists only practices with posts, so a new chat becomes
  // eligible once its first turn is saved. Refresh the lists then, once per
  // practice, instead of waiting for the chat page to unmount.
  const refreshRecentChats = useCallback(() => {
    if (!resolvedPracticeIdNum) return;
    if (recentChatsRefreshedForRef.current === resolvedPracticeIdNum) return;
    recentChatsRefreshedForRef.current = resolvedPracticeIdNum;
    void queryClient.invalidateQueries({ queryKey: ['practices', 'list'] });
  }, [queryClient, resolvedPracticeIdNum]);

  const handleDeliverySuccess = useCallback(
    (userMessageId: string) => {
      messageDelivery.completeDelivery();
      setMessageDeliveryStatus(userMessageId, 'delivered');
    },
    [messageDelivery, setMessageDeliveryStatus]
  );

  const deliverAssistantResponse = useCallback(
    (
      response: string,
      assistantMessageId: string,
      userMessageId: string,
      options?: { speak?: boolean }
    ) => {
      handleDeliverySuccess(userMessageId);
      addMessage(response, false, assistantMessageId);
      finishAssistantResponse();
      refreshRecentChats();
      if (options?.speak && enableTTS) {
        speakResponse(response, assistantMessageId);
      }
    },
    [
      addMessage,
      enableTTS,
      finishAssistantResponse,
      handleDeliverySuccess,
      refreshRecentChats,
      speakResponse,
    ]
  );

  const handleStreamingFirstDisplay = useCallback(() => {
    setThinking(false);
  }, [setThinking]);

  // Recovery checks run while the connection may be down: no global error
  // toast per attempt, and no query retries, since the callers poll or show
  // their own message.
  const fetchPostsQuietly = useCallback(async (): Promise<{
    data?: { results?: ApiPost[] };
    isError: boolean;
  }> => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['posts', resolvedPracticeIdNum],
        queryFn: () =>
          PracticeService.getPosts(resolvedPracticeIdNum, {
            suppressErrorToast: true,
          }),
        retry: false,
        staleTime: 0,
      });
      return { data, isError: false };
    } catch {
      return { data: undefined, isError: true };
    }
  }, [queryClient, resolvedPracticeIdNum]);

  // Recovery runs from callbacks created when the message was sent, so it
  // reads the live chat rather than that render's `messages`.
  const applyRecoveredPosts = useCallback(
    (postResults: ApiPost[]) => {
      const liveMessages = useChatStore.getState().messages;
      // Only a saved reply to the turn being recovered may replace the chat.
      // A partial reply shown locally is replaced by the full saved one.
      if (!hasPersistedReplyForLatestTurn(liveMessages, postResults)) {
        return false;
      }

      reconciledPostsKeyRef.current = getPostsReconciliationKey(
        resolvedPracticeId,
        postResults
      );
      clearMessages();
      addMessages(mapPostsToMessages(postResults), true);
      finishAssistantResponse();
      messageDelivery.completeDelivery();
      refreshRecentChats();
      // The turn is settled; stop any recovery still polling for it.
      recoveryAbortGenerationRef.current = Date.now();
      return true;
    },
    [
      addMessages,
      clearMessages,
      finishAssistantResponse,
      refreshRecentChats,
      messageDelivery,
      resolvedPracticeId,
    ]
  );

  const recoverTurn = useCallback(
    async (
      activeTurn: ActiveStreamTurn | null,
      options?: { afterTimeout?: boolean }
    ): Promise<boolean> => {
      const recoveryGeneration = activeTurn?.generation ?? Date.now();

      const recoveredPosts = await refetchUntilAssistantReply(
        fetchPostsQuietly,
        {
          delaysMs: options?.afterTimeout
            ? AFTER_TIMEOUT_RECOVERY_DELAYS_MS
            : effectiveLongSession
              ? SOULMAP_STREAM_RECOVERY_DELAYS_MS
              : undefined,
          shouldAbort: () =>
            recoveryAbortGenerationRef.current > recoveryGeneration ||
            activeStreamTurnRef.current !== activeTurn ||
            activeStreamTurnRef.current?.status === 'completed',
          isAnswered: (postResults) =>
            hasPersistedReplyForLatestTurn(
              useChatStore.getState().messages,
              postResults
            ),
        }
      );

      if (!recoveredPosts) {
        return false;
      }

      if (
        activeStreamTurnRef.current !== activeTurn ||
        activeStreamTurnRef.current?.status === 'completed'
      ) {
        return false;
      }

      const applied = applyRecoveredPosts(recoveredPosts);
      if (applied) {
        toast.success('Response recovered from the server.');
      }
      return applied;
    },
    [applyRecoveredPosts, effectiveLongSession, fetchPostsQuietly]
  );

  /**
   * Check the server for a saved reply to the current turn. After a timeout
   * the full wait has already passed, so one quick check is made; after a
   * dropped connection the server may still be finishing, so it polls.
   */
  const attemptRecoverInterruptedStream = useCallback(
    async (options?: { afterTimeout?: boolean }): Promise<boolean> => {
      if (!resolvedPracticeIdNum) {
        return false;
      }

      const activeTurn = activeStreamTurnRef.current;
      if (activeTurn?.status === 'completed') {
        return false;
      }

      if (
        activeTurn?.status === 'streaming' &&
        (streamingMessageId || streamingTargetTextRef.current.trim())
      ) {
        return false;
      }

      // The delivery timer and the stream timeout can fire together for the
      // same turn; they share one check instead of each running their own.
      const inFlight = recoveryInFlightRef.current;
      if (inFlight && inFlight.turn === activeTurn) {
        return inFlight.promise;
      }

      const promise = recoverTurn(activeTurn, options);
      recoveryInFlightRef.current = { turn: activeTurn, promise };
      void promise.finally(() => {
        if (recoveryInFlightRef.current?.promise === promise) {
          recoveryInFlightRef.current = null;
        }
      });
      return promise;
    },
    [recoverTurn, resolvedPracticeIdNum, streamingMessageId]
  );

  const getRetryPostId = useCallback((userMessageId: string) => {
    if (retryByTextOnlyRef.current.has(userMessageId)) return undefined;
    const savedPostId = userPostIdsRef.current.get(userMessageId);
    if (savedPostId != null) return savedPostId;
    // Messages rebuilt from the server use the post id as their id.
    return /^\d+$/.test(userMessageId) ? Number(userMessageId) : undefined;
  }, []);

  const handleStreamError = useCallback(
    async (
      userMessageId: string,
      streamMessageId: string,
      error: string,
      details?: StreamErrorDetails
    ) => {
      if (details?.postId != null) {
        userPostIdsRef.current.set(userMessageId, details.postId);
      }
      if (details?.code === 'invalid_retry') {
        retryByTextOnlyRef.current.add(userMessageId);
      }

      const activeTurn = activeStreamTurnRef.current;
      if (
        activeTurn?.streamMessageId === streamMessageId &&
        activeTurn.status === 'completed'
      ) {
        return;
      }

      if (activeTurn?.streamMessageId === streamMessageId) {
        activeTurn.status = 'failed';
      }

      // The stream is over; stop the delivery timer so it does not start a
      // second recovery alongside this one.
      messageDelivery.completeDelivery();

      const partialContent = streamingTargetTextRef.current;
      const hasPartialContent = Boolean(partialContent.trim());
      const errorMessage = resolveTurnErrorMessage(
        error,
        hasPartialContent,
        details
      );

      streamingTargetTextRef.current = '';
      setStreamingTargetText('');

      if (hasPartialContent) {
        updateMessage(streamMessageId, partialContent);
        finishAssistantResponse();
        // Offer Retry response straight away; the check below keeps looking
        // for the saved reply in the background.
        setMessageDeliveryStatus(userMessageId, 'interrupted', errorMessage);
      } else {
        removeMessage(streamMessageId);
      }

      // After a timeout, or when the server reported the failure itself or
      // the message never left, no reply is still on its way: one quick check
      // for a saved reply, then show Retry.
      const recovered = await attemptRecoverInterruptedStream({
        afterTimeout:
          error === RESPONSE_TIMEOUT_ERROR ||
          error === STREAM_TIMEOUT_ERROR ||
          Boolean(details?.code),
      });
      if (recovered) {
        return;
      }

      // The turn may have been settled meanwhile: a newer message was sent,
      // or Retry response recovered or resent it.
      const liveUserMessage = useChatStore
        .getState()
        .messages.find((item) => item.id === userMessageId);
      if (activeStreamTurnRef.current !== activeTurn || !liveUserMessage) {
        return;
      }

      if (hasPartialContent) {
        if (liveUserMessage.deliveryStatus === 'interrupted') {
          toast(errorMessage, { icon: '⚠️' });
        }
        return;
      }

      handleDeliveryFailure(userMessageId, errorMessage);
    },
    [
      attemptRecoverInterruptedStream,
      finishAssistantResponse,
      handleDeliveryFailure,
      messageDelivery,
      removeMessage,
      setMessageDeliveryStatus,
      updateMessage,
    ]
  );

  const deliverUserMessage = useCallback(
    async (text: string, existingUserMessageId?: string) => {
      const userMessageId = existingUserMessageId ?? `${Date.now()}-user`;

      if (
        isThinking ||
        messageDelivery.isDelivering() ||
        pendingSendRef.current
      ) {
        toast.error(
          'Please wait for the current response before sending another message.'
        );
        return;
      }

      // LE-338: the previous reply can still be streaming. Turn events carry
      // no turn id, so starting now would hand its remaining chunks and `end`
      // to this message. Show the message now and send it once that turn ends.
      const waitsForPreviousTurn =
        websocketService.isWebSocketEnabled() &&
        websocketService.hasActiveStream();

      if (waitsForPreviousTurn) {
        if (!existingUserMessageId) {
          addMessage(text, true, userMessageId);
        }
        setMessageDeliveryStatus(userMessageId, 'delivering');

        pendingSendRef.current = true;
        try {
          await websocketService.waitForActiveStreamToSettle();
        } finally {
          pendingSendRef.current = false;
        }

        // The chat was left or cleared meanwhile; do not send into it.
        const stillShown = useChatStore
          .getState()
          .messages.some((item) => item.id === userMessageId);
        if (!stillShown) {
          return;
        }
      }

      // Set only after the wait: the previous turn's completion clears the
      // thinking state and the delivery timer.
      setResponseInProgress(true);
      setThinking(true);

      if (!waitsForPreviousTurn) {
        if (!existingUserMessageId) {
          addMessage(text, true, userMessageId);
        }

        setMessageDeliveryStatus(userMessageId, 'delivering');
      }
      messageDelivery.beginDelivery(userMessageId, () => {
        void (async () => {
          const activeTurn = activeStreamTurnRef.current;
          if (activeTurn?.userMessageId === userMessageId) {
            if (activeTurn.status === 'completed') {
              return;
            }

            if (
              activeTurn.status === 'streaming' &&
              (streamingMessageId || streamingTargetTextRef.current.trim())
            ) {
              return;
            }
          }

          const recovered = await attemptRecoverInterruptedStream({
            afterTimeout: true,
          });
          if (recovered) {
            return;
          }

          if (activeStreamTurnRef.current?.status === 'completed') {
            return;
          }

          handleDeliveryFailure(userMessageId, NO_RESPONSE_DELIVERY_ERROR);
        })();
      });

      try {
        if (!resolvedPracticeId) {
          handleDeliveryFailure(
            userMessageId,
            'Unable to send your message. Please restart the conversation and try again.'
          );
          return;
        }

        const docIds =
          selectedFiles
            ?.map(
              (item) =>
                (
                  item as unknown as {
                    latest_snapshot?: { id?: number };
                  }
                ).latest_snapshot?.id
            )
            .filter(Boolean) || [];
        const resolvedAction = actionTitle;
        const docId = docIds.length > 0 ? docIds[0] : undefined;

        if (websocketService.isWebSocketEnabled()) {
          const streamMessageId = Date.now().toString() + '-stream';
          const streamGeneration = Date.now();
          activeStreamTurnRef.current = {
            generation: streamGeneration,
            streamMessageId,
            userMessageId,
            status: 'streaming',
          };
          let isFirstChunk = true;
          const retryPostId = existingUserMessageId
            ? getRetryPostId(existingUserMessageId)
            : undefined;
          // While startStream runs, a message that cannot be sent is handed
          // to the REST fallback below. A later `not_sent` (the service's own
          // stream restart) has no fallback and fails the turn as usual.
          let restFallbackPending = true;

          const messageId = await websocketService.startStream(
            resolvedPracticeIdNum,
            resolvedAction,
            text,
            {
              onStart: () => {
                setThinking(true);
              },
              onSoulMapDocumentQueued: handleSoulMapDocumentQueued,
              onUserPostSaved: (postId: number) => {
                userPostIdsRef.current.set(userMessageId, postId);
                // The message is saved, so stop showing "Sending..." while
                // the reply is generated.
                const liveUserMessage = useChatStore
                  .getState()
                  .messages.find((item) => item.id === userMessageId);
                if (liveUserMessage?.deliveryStatus === 'delivering') {
                  setMessageDeliveryStatus(userMessageId, 'delivered');
                }
              },
              onChunk: (content: string) => {
                if (isFirstChunk) {
                  handleDeliverySuccess(userMessageId);
                  addMessage('', false, streamMessageId);
                  setStreamingMessageId(streamMessageId);
                  syncStreamingTargetText(content);
                  isFirstChunk = false;
                  return;
                }

                syncStreamingTargetText(content);
              },
              onComplete: (response) => {
                // LE-338: a turn with no reply text is a failure, whichever
                // layer let it through; never mark the message delivered.
                if (!response.trim()) {
                  void handleStreamError(
                    userMessageId,
                    streamMessageId,
                    EMPTY_RESPONSE_ERROR,
                    { code: 'empty_response' }
                  );
                  return;
                }

                const activeTurn = activeStreamTurnRef.current;
                if (activeTurn?.streamMessageId === streamMessageId) {
                  activeTurn.status = 'completed';
                  recoveryAbortGenerationRef.current = activeTurn.generation;
                }

                updateMessage(streamMessageId, response);
                streamingTargetTextRef.current = '';
                setStreamingTargetText('');
                finishAssistantResponse();
                messageDelivery.completeDelivery();
                setMessageDeliveryStatus(userMessageId, 'delivered');
                refreshRecentChats();
                if (enableTTS) {
                  speakResponse(response, streamMessageId);
                }
              },
              onError: (error: string, details?: StreamErrorDetails) => {
                // The message never reached the server: the REST fallback
                // below sends it instead, so this turn is not failed here.
                if (details?.code === 'not_sent' && restFallbackPending) {
                  return;
                }
                void handleStreamError(
                  userMessageId,
                  streamMessageId,
                  error,
                  details
                );
              },
            },
            docId,
            true,
            { retryPostId }
          );
          restFallbackPending = false;

          if (!messageId) {
            // The socket could not carry the message, so nothing was saved:
            // send it over REST. Failures land in the catch below (Retry).
            const response = await runPracticeMutation.mutateAsync({
              practiceId: resolvedPracticeIdNum,
              userMessage: text,
              action: resolvedAction,
              docId: docId ?? 0,
            });

            if (
              activeStreamTurnRef.current?.streamMessageId === streamMessageId
            ) {
              activeStreamTurnRef.current.status = 'completed';
            }
            deliverAssistantResponse(
              response,
              Date.now().toString(),
              userMessageId,
              { speak: true }
            );
          }

          return;
        }

        const response = await runPracticeMutation.mutateAsync({
          practiceId: resolvedPracticeIdNum,
          userMessage: text,
          action: resolvedAction,
          docId: docId ?? 0,
        });

        deliverAssistantResponse(
          response,
          Date.now().toString(),
          userMessageId,
          { speak: true }
        );
      } catch (error) {
        console.error('Error in chat:', error);
        handleDeliveryFailure(
          userMessageId,
          'Sorry, there was an error processing your message. Please try again.'
        );
      }
    },
    [
      actionTitle,
      addMessage,
      attemptRecoverInterruptedStream,
      deliverAssistantResponse,
      enableTTS,
      finishAssistantResponse,
      getRetryPostId,
      handleDeliveryFailure,
      handleDeliverySuccess,
      handleStreamError,
      handleSoulMapDocumentQueued,
      isThinking,
      messageDelivery,
      refreshRecentChats,
      resolvedPracticeId,
      resolvedPracticeIdNum,
      runPracticeMutation,
      removeMessage,
      selectedFiles,
      setMessageDeliveryStatus,
      setResponseInProgress,
      setStreamingMessageId,
      setThinking,
      speakResponse,
      syncStreamingTargetText,
      updateMessage,
    ]
  );

  // LE-338: a phone that slept or lost signal mid-reply can come back to a
  // turn that will never finish on its own. When the page is visible or
  // online again: a closed socket fails the turn (which checks for a saved
  // reply, else offers Retry); an open one may still have lost the reply, so
  // check whether the server already saved it. The server answers nothing
  // else while it generates, so a ping cannot tell a slow reply from a dead
  // socket; the response timeout covers that case.
  const checkTurnAfterResume = useCallback(async () => {
    if (!websocketService.hasActiveStream()) return;
    if (websocketService.failActiveStreamIfDisconnected()) return;

    const turn = activeStreamTurnRef.current;
    if (!turn || turn.status !== 'streaming' || !resolvedPracticeIdNum) return;

    const result = await fetchPostsQuietly();
    if (result.isError) return;
    if (activeStreamTurnRef.current !== turn || turn.status !== 'streaming') {
      return;
    }

    if (applyRecoveredPosts(result.data?.results ?? [])) {
      turn.status = 'completed';
      websocketService.abandonActiveStream();
      // The abandoned stream may still have carried a document-queued event.
      if (isSoulMapPractice) {
        void refetchSoulMapDocuments();
      }
      toast.success('Response recovered from the server.');
    }
  }, [
    applyRecoveredPosts,
    fetchPostsQuietly,
    isSoulMapPractice,
    refetchSoulMapDocuments,
    resolvedPracticeIdNum,
  ]);

  useEffect(() => {
    if (!websocketService.isWebSocketEnabled()) return;

    const onResume = () => {
      if (document.visibilityState !== 'visible') return;
      void checkTurnAfterResume();
    };

    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onResume);
    };
  }, [checkTurnAfterResume]);

  // Returns whether the message was taken, so the input keeps a refused
  // message instead of clearing it.
  const handleSendMessage = useCallback(
    (text: string) => {
      if (
        isThinking ||
        messageDelivery.isDelivering() ||
        pendingSendRef.current
      ) {
        toast.error(
          'Please wait for the current response before sending another message.'
        );
        return false;
      }

      void deliverUserMessage(text);
      return true;
    },
    [deliverUserMessage, isThinking, messageDelivery]
  );

  const handleRetryMessage = useCallback(
    (messageId: string) => {
      const message = messages.find((item) => item.id === messageId);
      if (!message?.isUser) {
        return;
      }

      if (
        message.deliveryStatus !== 'interrupted' &&
        message.deliveryStatus !== 'failed'
      ) {
        return;
      }

      if (
        isThinking ||
        messageDelivery.isDelivering() ||
        pendingSendRef.current
      ) {
        toast.error(
          'Please wait for the current response before sending another message.'
        );
        return;
      }

      // Without a practice there are no posts to check; delivery reports it.
      if (!resolvedPracticeIdNum) {
        void deliverUserMessage(message.text, messageId);
        return;
      }

      const previousStatus = message.deliveryStatus;
      const previousError = message.deliveryError;

      void (async () => {
        setMessageDeliveryStatus(messageId, 'delivering');

        // Check the server first: the reply may have been saved after the
        // connection dropped, and sending again would store the turn twice.
        const result = await fetchPostsQuietly();
        if (result.isError) {
          setMessageDeliveryStatus(messageId, previousStatus, previousError);
          finishAssistantResponse();
          toast.error(
            'Could not reach the server. Check your connection and try again.'
          );
          return;
        }

        if (applyRecoveredPosts(result.data?.results ?? [])) {
          toast.success('Response recovered from the server.');
          return;
        }

        // No reply was saved: ask again. The saved post is answered by id
        // when known; otherwise the backend reuses an already-saved,
        // unanswered post with the same text instead of storing it twice.
        void deliverUserMessage(message.text, messageId);
      })();
    },
    [
      applyRecoveredPosts,
      deliverUserMessage,
      fetchPostsQuietly,
      finishAssistantResponse,
      isThinking,
      messageDelivery,
      messages,
      resolvedPracticeIdNum,
      setMessageDeliveryStatus,
    ]
  );

  const handleLeaveChat = useCallback(() => {
    guardAction(() => {
      clearMessages();
      if (onLeaveChat) {
        onLeaveChat();
        return;
      }
      navigateToHome({ replace: true, hubTab: urlHubTab });
    });
  }, [clearMessages, guardAction, navigateToHome, onLeaveChat, urlHubTab]);

  const isGlobalLoading = useLoaderStore((state) => state.isLoading);
  const leftMissingPracticeRef = useRef(false);
  useEffect(() => {
    if (!isPracticeNotFound || leftMissingPracticeRef.current) return;
    // navigateToHome does nothing while the global loader shows (e.g. SSO
    // setup on a reload), so wait for it rather than strand the spinner.
    if (isGlobalLoading && !onLeaveChat) return;
    leftMissingPracticeRef.current = true;

    // A fixed id keeps Strict Mode's double effect to a single toast.
    toast.error("This chat isn't available.", { id: 'practice-not-found' });
    clearMessages();
    if (onLeaveChat) {
      onLeaveChat();
      return;
    }
    navigateToHome({ replace: true, hubTab: urlHubTab });
  }, [
    clearMessages,
    isGlobalLoading,
    isPracticeNotFound,
    navigateToHome,
    onLeaveChat,
    urlHubTab,
  ]);

  const reStart = useCallback(() => {
    guardAction(() => {
      reconciledPostsKeyRef.current = null;
      clearMessages();
      queryClient.removeQueries({ queryKey: ['posts', resolvedPracticeIdNum] });
      restartPractice(actionTitle);
    });
  }, [
    actionTitle,
    clearMessages,
    guardAction,
    resolvedPracticeIdNum,
    queryClient,
    restartPractice,
  ]);

  useEffect(() => {
    if (!isSuccess || !posts || !isPostsFetchedAfterMount) return;
    if (needsPracticeLoad) return;

    if (posts.results.length > 0) {
      const reconciliationKey = getPostsReconciliationKey(
        resolvedPracticeId,
        posts.results
      );
      // The local welcome message is only a placeholder for an empty chat;
      // stored history always replaces it.
      const localMessages =
        messages.length === 1 && messages[0].id === welcomeMessage.id
          ? []
          : messages;

      if (
        reconciledPostsKeyRef.current === reconciliationKey &&
        localMessages.length === 0
      ) {
        reconciledPostsKeyRef.current = null;
      }

      if (reconciledPostsKeyRef.current === reconciliationKey) return;

      if (streamingMessageId || isThinking) {
        return;
      }

      if (isLocalConversationComplete(localMessages, posts.results)) {
        reconciledPostsKeyRef.current = reconciliationKey;
        return;
      }

      if (!shouldRebuildFromServerPosts(localMessages, posts.results)) {
        reconciledPostsKeyRef.current = reconciliationKey;
        return;
      }

      reconciledPostsKeyRef.current = reconciliationKey;

      const postMessages = mapPostsToMessages(posts.results);
      clearMessages();
      addMessages(postMessages, true);

      const interruptedMessageId = getInterruptedUserMessageId(postMessages);
      if (interruptedMessageId) {
        setMessageDeliveryStatus(
          interruptedMessageId,
          'interrupted',
          INTERRUPTED_RESPONSE_ERROR
        );

        if (!hasShownInterruptedToastRef.current) {
          hasShownInterruptedToastRef.current = true;
          toast(
            'Your last reply did not finish. Tap Retry response to send it again.',
            { icon: '⚠️' }
          );
        }
      }
      return;
    }

    const emptyReconciliationKey = getPostsReconciliationKey(
      resolvedPracticeId,
      []
    );

    if (reconciledPostsKeyRef.current === emptyReconciliationKey) return;

    reconciledPostsKeyRef.current = emptyReconciliationKey;

    if (!messages.length) {
      addMessage(
        tenantConfig?.agent_first_message || welcomeMessage.question,
        false,
        welcomeMessage.id
      );
    }
  }, [
    addMessage,
    addMessages,
    clearMessages,
    isPostsFetchedAfterMount,
    isSuccess,
    isThinking,
    messages,
    needsPracticeLoad,
    posts,
    resolvedPracticeId,
    setMessageDeliveryStatus,
    streamingMessageId,
    tenantConfig?.agent_first_message,
  ]);

  useEffect(() => {
    hasShownInterruptedToastRef.current = false;
    reconciledPostsKeyRef.current = null;
    activeStreamTurnRef.current = null;
    recoveryAbortGenerationRef.current = 0;
  }, [resolvedPracticeId]);

  // Disconnect WebSocket and clear chat state when leaving this practice (not on Strict Mode remounts)
  useEffect(() => {
    const activePracticeId = resolvedPracticeId;

    return () => {
      if (
        activePracticeId &&
        window.location.pathname.includes(`/${activePracticeId}/run`)
      ) {
        return;
      }

      clearMessages();
      reconciledPostsKeyRef.current = null;
      websocketService.sendMessage({ event: 'close' }).catch(console.error);
      websocketService.disconnect();
    };
  }, [clearMessages, resolvedPracticeId]);

  // Bottom padding for messages is handled via CSS classes

  return (
    <>
      {/* Main container - h-full on mobile to fill parent, calc height on desktop */}
      <div className="relative flex w-full h-full md:h-[calc(100vh-80px)] overflow-hidden">
        {/* Main chat area */}
        <div className="flex-1 flex flex-col h-full overflow-hidden bg-white dark:bg-default-100">
          {/* Chat Header - sticky at top of chat area */}
          <div className="fixed top-[64px] w-full md:sticky md:top-0 flex-shrink-0 bg-white dark:bg-default-100 md:py-2 z-20 px-2 md:px-4 py-4 md:p-0 pr-6">
            <ChatHeader
              actionTitle={actionTitle || runActionState.actionTitle}
              reStart={reStart}
              currentVoice={currentVoice}
              setCurrentVoice={setCurrentVoice}
              availableVoices={availableVoices}
              setIsSettingsOpen={setIsSettingsOpen}
              onBackButtonClick={handleLeaveChat}
              practiceId={resolvedPracticeId ?? ''}
              practiceCreatedAt={practice?.created_at}
              soulMapDocuments={soulMapDocuments}
              treatExpiredDocumentAsPreparing={awaitingDocumentRefresh}
              regenerateExpiredDocument={birthDetailsOptedOut}
              onReviewBirthDetails={handleReviewBirthDetails}
              showRestartButton={showRestartButton}
            />
          </div>

          {/* Messages area - mt clears the fixed chat header on mobile, which grows by a row when the Soul Map document pill shows */}
          <div
            className={`flex-1 min-h-0 overflow-y-auto pb-8 md:pb-4 md:mt-0 pl-2 md:pl-0 ${
              showSoulMapHeaderPill ? 'mt-[146px]' : 'mt-[90px]'
            }`}
          >
            <div className="flex flex-col gap-4 p-0 md:p-4 ">
              {initialisingChat ||
              fetchingLatetConversation ||
              needsInitialPostsLoad ||
              needsPracticeLoad ? (
                <div className="flex flex-col items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-neutral-100"></div>
                  <p className="mt-2 text-neutral-600 dark:text-neutral-400">
                    {initialisingChat
                      ? `Initiating chat...`
                      : 'Fetching conversation..'}
                  </p>
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  isThinking={isThinking}
                  playingMessageId={playingMessageId}
                  togglePlayPause={togglePlayPause}
                  enableTTS={enableTTS}
                  loadingMessageId={loadingMessageId}
                  streamingMessageId={streamingMessageId}
                  streamingTargetText={streamingTargetText}
                  onRetryMessage={handleRetryMessage}
                  onStreamingFirstDisplay={handleStreamingFirstDisplay}
                  messageAttachment={
                    soulMapDocumentAnchorId && resolvedPracticeId
                      ? {
                          messageId: soulMapDocumentAnchorId,
                          content: (
                            <SoulMapDocumentCard
                              practiceId={String(resolvedPracticeId)}
                              statusQuery={soulMapDocuments}
                              treatExpiredAsPreparing={awaitingDocumentRefresh}
                              regenerateExpired={birthDetailsOptedOut}
                              onReviewBirthDetails={handleReviewBirthDetails}
                              animateReveal={revealSoulMapDocument}
                            />
                          ),
                        }
                      : null
                  }
                />
              )}
              {/*{isSoulMapPractice && resolvedPracticeId ? (*/}
              {/*  <SoulMapDocumentControls*/}
              {/*    practiceId={String(resolvedPracticeId)}*/}
              {/*    statusQuery={soulMapDocuments}*/}
              {/*  />*/}
              {/*) : null}*/}
            </div>
          </div>

          {/* Desktop input */}
          {!initialisingChat && (
            <div className="hidden md:block flex-shrink-0 bg-white dark:bg-default-100 px-4 py-3 border-gray-100 mb-4">
              <div className="w-[80%] mx-auto">
                <InputBox
                  onSendMessage={handleSendMessage}
                  isThinking={isThinking}
                  isSpeaking={!!playingMessageId}
                  isInitiatedSpeaking={!!loadingMessageId}
                  stopSpeaking={stopSpeaking}
                  setIsSettingsOpen={setIsSettingsOpen}
                  onSaveToMemory={handleSaveToMemory}
                  isSavingMemory={isSavingMemory}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sidepanel - desktop only */}
        {enableQuickActions && (
          <ChatSidepanel
            onActionClick={handleSendMessage}
            hasMessages={messages.length > 0}
            actionTitle={actionTitle || runActionState.actionTitle}
            userProfile={userProfile}
          />
        )}
      </div>

      {/* MOBILE ONLY: Fixed input bar with keyboard detection */}
      {!initialisingChat && (
        <div
          className="fixed left-4 right-4 z-[999] flex justify-center md:hidden"
          style={{
            bottom: isKeyboardVisible
              ? '36px'
              : 'calc(56px + env(safe-area-inset-bottom, 0px) + 12px)',
          }}
        >
          <div className="w-full max-w-full">
            <InputBox
              onSendMessage={handleSendMessage}
              isThinking={isThinking}
              isSpeaking={!!playingMessageId}
              isInitiatedSpeaking={!!loadingMessageId}
              stopSpeaking={stopSpeaking}
              setIsSettingsOpen={setIsSettingsOpen}
              onSaveToMemory={handleSaveToMemory}
              isSavingMemory={isSavingMemory}
            />
          </div>
        </div>
      )}

      <SettingsDrawer
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      {profileConfirmationEnabled && (
        <SoulMapProfileConfirmModal
          isOpen={isProfileConfirmOpen}
          mode={profileModalMode}
          initialForm={soulMapProfileForm}
          storedBirthPlaceLabel={storedBirthPlaceLabel}
          isLoading={isLoadingProfileForm}
          isSubmitting={isSavingProfile}
          serverErrors={profileServerErrors}
          onSubmit={handleProfileConfirmSubmit}
          onExit={handleProfileConfirmExit}
        />
      )}
    </>
  );
}

export default ChatContainer;
